/*
  React 19 removed several ReactDOM APIs that dependencies we still ship call at
  runtime as properties of the react-dom default export. This module restores
  best-effort replacements and assigns them back onto that export. It is imported
  as a side-effect from every React-mounting pack (components.js, admin.js,
  assessments.js) so any island is protected before it mounts.

  Removed APIs restored here and who still calls them:

    - findDOMNode
      - @salesforce/design-system-react (SLDS, rendered through @devforce/tds-react)
        in its overlay components: tooltip, popover, menu-dropdown, combobox,
        date-picker. These back our Tooltip, DropdownMenu, Combobox, etc.
      - react-onclickoutside (transitive via those SLDS overlays).
      - react-sortable-hoc (the trailmix edit / drag-sort content list).

    - unstable_renderSubtreeIntoContainer / unmountComponentAtNode
      - @salesforce/design-system-react's own dialog Portal (dialog/portal.js),
        which mounts every SLDS overlay into a detached node. This is separate
        from the npm `react-portal` package, which branches on createPortal and
        so is unaffected.

  IMPORTANT: mutating the default export only rescues callers that read the API
  as a *property* (`ReactDOM.findDOMNode(...)`). Dependencies whose ESM build does
  a static `import { findDOMNode } from 'react-dom'` bind to the now-missing named
  export at module-eval time and cannot be patched afterward; those
  (react-sortable-hoc, react-onclickoutside) are aliased to their CJS builds in
  vite.config.mts so they use property access instead. SLDS reads all three APIs
  as properties, so it needs no alias.

  CAVEAT: findDOMNode relies on React 19 private internals (`_reactInternals`),
  which are undocumented and may change across 19.x releases. This is a bridge for
  deprecated SLDS/tds-react, not a permanent solution — the real fix is migrating
  off those components (tracked separately). Revisit this shim on any React minor
  bump.
*/
import ReactDOM from 'react-dom';
import { renderInContainer, unmountContainer } from 'lib/domRoot';

const findHostNode = (fiber) => {
  let node = fiber;
  while (node) {
    if (node.stateNode && node.stateNode.nodeType === 1) {
      return node.stateNode;
    }
    node = node.child;
  }
  return null;
};

/* eslint-disable no-underscore-dangle */
const getFiber = (instance) =>
  instance._reactInternals ||
  instance._reactInternalFiber ||
  (instance.stateNode && instance.stateNode._reactInternals);
/* eslint-enable no-underscore-dangle */

if (typeof ReactDOM.findDOMNode !== 'function') {
  ReactDOM.findDOMNode = (instance) => {
    if (instance == null) return null;
    if (instance.nodeType === 1) return instance;

    const fiber = getFiber(instance);
    return fiber ? findHostNode(fiber) : null;
  };
}

// SLDS's dialog Portal used the legacy imperative mount pair. createRoot renders
// asynchronously, so the caller's completion callback (SLDS uses it only to fire
// onOpen and flip isOpen) is invoked on a microtask once the render is scheduled.
// The return value is unused by SLDS, so null is safe.
if (typeof ReactDOM.unstable_renderSubtreeIntoContainer !== 'function') {
  ReactDOM.unstable_renderSubtreeIntoContainer = (_parent, element, container, callback) => {
    renderInContainer(element, container);
    if (typeof callback === 'function') {
      Promise.resolve().then(callback);
    }
    return null;
  };
}

// Deliberately restoring the removed API is the whole point of this shim.
/* eslint-disable react/no-deprecated */
if (typeof ReactDOM.unmountComponentAtNode !== 'function') {
  ReactDOM.unmountComponentAtNode = (container) => unmountContainer(container);
}
/* eslint-enable react/no-deprecated */
